import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Container,
  Text,
  VStack,
  HStack,
  Input,
  Textarea,
  Select,
  Button,
  FormControl,
  FormLabel,
  useColorModeValue,
  SimpleGrid,
  Grid,
  useToast,
  Checkbox,
  Flex,
} from "@chakra-ui/react";
import ReCAPTCHA from "react-google-recaptcha";
import { contactService } from "../../services/contactService";
import { contactFormDataService } from "../../services/contactFormDataService";

const ContactForm = ({ data, initialTab = "product" }) => {
  const [activeTab, setActiveTab] = useState(initialTab); // 'product', 'partnership', 'support'

  // Update activeTab when initialTab prop changes
  React.useEffect(() => {
    if (
      initialTab &&
      ["product", "partnership", "support"].includes(initialTab)
    ) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  // Check if reCAPTCHA site key is available
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  if (!recaptchaSiteKey) {
    console.warn("reCAPTCHA site key not found in environment variables");
  }
  const [formData, setFormData] = useState({
    // Common fields
    fullName: "",
    email: "",
    phoneNumber: "",
    organizationName: "",
    // Product specific
    productType: "",
    hearAboutUs: "",
    productUsage: "",
    // Partnership specific
    designation: "",
    partnershipTypes: [],
    companyWebsite: "",
    otherPartnershipText: "",
    // Support specific - will be handled separately
    privacyAgreed: false,
    contactConsent: false,
  });

  // State for dropdown data
  const [dropdownData, setDropdownData] = useState({
    solutions: [],
    hearUs: [],
  });
  const [isLoadingDropdownData, setIsLoadingDropdownData] = useState(true);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  const toast = useToast();

  // Fetch dropdown data on component mount
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        setIsLoadingDropdownData(true);
        const response = await contactFormDataService.getContactFormData();
        
        if (response.success) {
          const transformedData = contactFormDataService.transformFormData(response.data);
          setDropdownData(transformedData);
        } else {
          console.warn('Failed to fetch dropdown data:', response.error);
        }
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
      } finally {
        setIsLoadingDropdownData(false);
      }
    };

    fetchDropdownData();
  }, []);

  const formBg = useColorModeValue("#EFF6FF", "gray.700");
  const labelColor = useColorModeValue("gray.600", "gray.300");
  const borderColor = useColorModeValue("#e2e8f0", "gray.600");
  const inputBg = useColorModeValue("white", "gray.600");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleRecaptchaError = () => {
    console.warn("reCAPTCHA error occurred");
    setRecaptchaToken(null);
    toast({
      title: "reCAPTCHA Error",
      description:
        "There was an error loading reCAPTCHA. Please refresh the page.",
      status: "warning",
      duration: 5000,
      isClosable: true,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.fullName || !formData.email) {
      toast({
        title: "Missing Information",
        description: "Please fill in your name and email address.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Privacy policy and contact consent validation
    if (!formData.privacyAgreed) {
      toast({
        title: "Agreement Required",
        description:
          "Please agree to the data privacy policy and terms to continue.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!formData.contactConsent) {
      toast({
        title: "Consent Required",
        description:
          "Please provide consent for us to contact you regarding your inquiry.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Tab-specific validation
    if (activeTab === "product" && !formData.productType) {
      toast({
        title: "Missing Information",
        description: "Please select a product/solution type.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (
      activeTab === "partnership" &&
      (!formData.designation || !formData.organizationName)
    ) {
      toast({
        title: "Missing Information",
        description: "Please fill in your designation and organization name.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // reCAPTCHA validation
    if (!recaptchaToken) {
      toast({
        title: "reCAPTCHA Required",
        description: "Please complete the reCAPTCHA verification.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await contactService.submitContactForm({
        ...formData,
        formType: activeTab,
        submissionDate: new Date().toISOString(),
      });

      if (response.success) {
        const successMessages = {
          product:
            "Thank you for your product inquiry. We will get back to you soon.",
          partnership:
            "Thank you! Our team will get back to you within 3 working days.",
          support:
            "Your support request has been submitted. Our team will assist you shortly.",
        };

        toast({
          title: "Message sent successfully!",
          description: successMessages[activeTab],
          status: "success",
          duration: 5000,
          isClosable: true,
        });

        // Reset form
        setFormData({
          fullName: "",
          email: "",
          phoneNumber: "",
          organizationName: "",
          productType: "",
          hearAboutUs: "",
          productUsage: "",
          designation: "",
          partnershipTypes: [],
          companyWebsite: "",
          otherPartnershipText: "",
          privacyAgreed: false,
          contactConsent: false,
        });

        // Reset reCAPTCHA
        setRecaptchaToken(null);
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      toast({
        title: "Error Sending Message",
        description: error.message || "Something went wrong. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });

      // Reset reCAPTCHA on error
      setRecaptchaToken(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle partnership type checkbox changes
  const handlePartnershipTypeChange = (type, checked) => {
    setFormData((prev) => ({
      ...prev,
      partnershipTypes: checked
        ? [...prev.partnershipTypes, type]
        : prev.partnershipTypes.filter((t) => t !== type),
    }));
  };

  return (
    <Container maxW="container.xl" py={8}>
      {/* Header Section */}
      <VStack spacing={6} mb={8} align="start" textAlign="start">
        <Text
          fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
          fontWeight="bold"
          color="#000B8C"
          lineHeight="1.2"
        >
          Let's Connect — We're Here to Help
        </Text>
        {/* <Text
          fontSize={{ base: "sm", md: "md" }}
          color={labelColor}
          maxW="2xl"
          lineHeight="1.6"
        >
          If you email us more than one paragraph, we will respond asking you to condense your message
        </Text> */}
      </VStack>

      {/* Contact Form */}
      <Box
        bg={formBg}
        borderRadius="xl"
        p={{ base: 4, sm: 6, md: 8, lg: 10 }}
        border="1px solid"
        borderColor={borderColor}
        maxW="7xl"
        mx="auto"
        width="100%"
      >
        {/* Tab Selection */}
        <VStack spacing={6}>
          <Flex justify="center" mb={{ base: 4, md: 6 }} width="100%">
            <HStack
              spacing={0}
              border="2px solid"
              borderColor="#000B8C"
              borderRadius="10px"
              overflow="hidden"
              bg="white"
              flexWrap={{ base: "wrap", sm: "nowrap" }}
              width={{ base: "100%", sm: "auto" }}
            >
              {[
                { id: "product", label: "Product / Solution" },
                { id: "partnership", label: "Partnership Collaboration" },
                { id: "support", label: "Support Portal" },
              ].map((tab) => (
                <Button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  bg={activeTab === tab.id ? "#000B8C" : "transparent"}
                  color={activeTab === tab.id ? "white" : "#000B8C"}
                  borderRadius="0"
                  px={{ base: 2, sm: 3, md: 4, lg: 6 }}
                  py={{ base: 2, md: 3 }}
                  fontSize={{ base: "xs", sm: "sm", md: "md" }}
                  fontWeight="medium"
                  border="none"
                  minW={{ base: "auto", sm: "120px" }}
                  flex={{ base: "1", sm: "none" }}
                  textAlign="center"
                  _hover={{
                    bg: activeTab === tab.id ? "#000B8C" : "gray.50",
                  }}
                  _focus={{ boxShadow: "none" }}
                >
                  {tab.label}
                </Button>
              ))}
            </HStack>
          </Flex>

          {/* Form Content Based on Active Tab */}
          <Box as="form" onSubmit={handleSubmit} width="100%">
            {activeTab === "product" && (
              <VStack spacing={4} width="100%">
                {/* Product/Solution Form */}
                <SimpleGrid
                  columns={{ base: 1, sm: 1, md: 2 }}
                  spacing={{ base: 3, md: 4 }}
                  width="100%"
                >
                  <FormControl isRequired>
                    <FormLabel
                      color={labelColor}
                      fontSize={{ base: "xs", md: "sm" }}
                      fontWeight="medium"
                      mb={2}
                    >
                      Full Name
                    </FormLabel>
                    <Input
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      placeholder="Enter"
                      bg={inputBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="md"
                      size={{ base: "sm", md: "md" }}
                      fontSize={{ base: "sm", md: "md" }}
                      _placeholder={{
                        color: "gray.400",
                        fontSize: { base: "sm", md: "md" },
                      }}
                      _focus={{
                        borderColor: "#000B8C",
                        boxShadow: "0 0 0 1px #000B8C",
                      }}
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel
                      color={labelColor}
                      fontSize="sm"
                      fontWeight="medium"
                      mb={2}
                    >
                      Organization Name
                    </FormLabel>
                    <Input
                      name="organizationName"
                      value={formData.organizationName}
                      onChange={handleInputChange}
                      placeholder="Enter"
                      bg={inputBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="md"
                      size="md"
                      _placeholder={{ color: "gray.400" }}
                      _focus={{
                        borderColor: "#000B8C",
                        boxShadow: "0 0 0 1px #000B8C",
                      }}
                    />
                  </FormControl>
                </SimpleGrid>

                <SimpleGrid
                  columns={{ base: 1, md: 2 }}
                  spacing={4}
                  width="100%"
                >
                  <FormControl isRequired>
                    <FormLabel
                      color={labelColor}
                      fontSize="sm"
                      fontWeight="medium"
                      mb={2}
                    >
                      Work Email Address
                    </FormLabel>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter"
                      bg={inputBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="md"
                      size="md"
                      _placeholder={{ color: "gray.400" }}
                      _focus={{
                        borderColor: "#000B8C",
                        boxShadow: "0 0 0 1px #000B8C",
                      }}
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel
                      color={labelColor}
                      fontSize="sm"
                      fontWeight="medium"
                      mb={2}
                    >
                      Phone Number
                    </FormLabel>
                    <Input
                      name="phoneNumber"
                      type="tel"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      placeholder="Enter"
                      bg={inputBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="md"
                      size="md"
                      _placeholder={{ color: "gray.400" }}
                      _focus={{
                        borderColor: "#000B8C",
                        boxShadow: "0 0 0 1px #000B8C",
                      }}
                    />
                  </FormControl>
                </SimpleGrid>

                <SimpleGrid
                  columns={{ base: 1, md: 2 }}
                  spacing={4}
                  width="100%"
                >
                  <FormControl isRequired>
                    <FormLabel
                      color={labelColor}
                      fontSize="sm"
                      fontWeight="medium"
                      mb={2}
                    >
                      Product/Solution Type
                    </FormLabel>
                    <Select
                      name="productType"
                      value={formData.productType}
                      onChange={handleInputChange}
                      placeholder="Select"
                      bg={inputBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="md"
                      size="md"
                      isDisabled={isLoadingDropdownData}
                      _focus={{
                        borderColor: "#000B8C",
                        boxShadow: "0 0 0 1px #000B8C",
                      }}
                    >
                      {dropdownData.solutions.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel
                      color={labelColor}
                      fontSize="sm"
                      fontWeight="medium"
                      mb={2}
                    >
                      How did you hear about us?
                    </FormLabel>
                    <Select
                      name="hearAboutUs"
                      value={formData.hearAboutUs}
                      onChange={handleInputChange}
                      placeholder="Select"
                      bg={inputBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="md"
                      size="md"
                      isDisabled={isLoadingDropdownData}
                      _focus={{
                        borderColor: "#000B8C",
                        boxShadow: "0 0 0 1px #000B8C",
                      }}
                    >
                      {dropdownData.hearUs.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </SimpleGrid>

                <FormControl>
                  <FormLabel
                    color={labelColor}
                    fontSize="sm"
                    fontWeight="medium"
                    mb={2}
                  >
                    Possible usage of the product / solution
                  </FormLabel>
                  <Textarea
                    name="productUsage"
                    value={formData.productUsage}
                    onChange={handleInputChange}
                    placeholder="Enter"
                    bg={inputBg}
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius="md"
                    minH="100px"
                    _placeholder={{ color: "gray.400" }}
                    _focus={{
                      borderColor: "#000B8C",
                      boxShadow: "0 0 0 1px #000B8C",
                    }}
                  />
                </FormControl>

                <VStack align="start" spacing={2} width="100%">
                  <Checkbox
                    isChecked={formData.contactConsent}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        contactConsent: e.target.checked,
                      }))
                    }
                    colorScheme="blue"
                    size="md"
                  >
                    <Text fontSize="sm" color={labelColor}>
                      I consent to be contacted by Business Automation Limited
                      regarding this inquiry.
                    </Text>
                  </Checkbox>

                  <Checkbox
                    isChecked={formData.privacyAgreed}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        privacyAgreed: e.target.checked,
                      }))
                    }
                    colorScheme="blue"
                    size="md"
                  >
                    <Text fontSize="sm" color={labelColor}>
                      I agree to the data privacy policy and terms.
                    </Text>
                  </Checkbox>
                </VStack>

                {/* reCAPTCHA */}
                <Box width="100%" display="flex" justifyContent="center">
                  {recaptchaSiteKey ? (
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={recaptchaSiteKey}
                      onChange={handleRecaptchaChange}
                      onExpired={() => setRecaptchaToken(null)}
                      onErrored={handleRecaptchaError}
                    />
                  ) : (
                    <Text color="red.500" fontSize="sm">
                      reCAPTCHA configuration error
                    </Text>
                  )}
                </Box>

                <Button
                  type="submit"
                  bg="#000B8C"
                  color="white"
                  size="lg"
                  px={8}
                  py={3}
                  borderRadius="full"
                  fontSize="md"
                  fontWeight="medium"
                  isLoading={isSubmitting}
                  isDisabled={
                    !formData.privacyAgreed ||
                    !formData.contactConsent ||
                    isSubmitting
                  }
                  loadingText="Sending..."
                  _hover={{
                    bg:
                      !formData.privacyAgreed || !formData.contactConsent
                        ? "#000B8C"
                        : "#000A7A",
                  }}
                  _active={{
                    bg: "#000968",
                  }}
                  _disabled={{
                    bg: "gray.400",
                    color: "gray.600",
                    cursor: "not-allowed",
                    opacity: 0.6,
                  }}
                >
                  Send My Product/Solution Inquiry
                </Button>
              </VStack>
            )}

            {activeTab === "partnership" && (
              <VStack spacing={4} width="100%">
                {/* Partnership Form */}
                <Box textAlign="justify" mb={4}>
                  <Text fontSize="md" color={labelColor} lineHeight="1.6">
                    <Text as="span" fontWeight="bold">
                      {" "}
                    </Text>{" "}
                    Here, at Business Automation, we believe the key to any
                    successful project is good partnership- whether that be
                    between team members or project partners. As such, we are
                    always on the lookout for potential partners to work on
                    exciting projects with. If you wish to know more about us
                    and what a partnership with us looks like, please click on
                    the following{" "}
                    <Text
                      as="span"
                      color="#000B8C"
                      cursor="pointer"
                      textDecoration="underline"
                      _hover={{ textDecoration: "none" }}
                      onClick={() =>
                        window.open("/partnerships-collaborations", "_blank")
                      }
                    >
                      link
                    </Text>
                    .
                  </Text>
                </Box>

                <Text
                  fontSize="lg"
                  fontWeight="semibold"
                  color={labelColor}
                  textAlign="center"
                >
                  Basic Information
                </Text>

                <SimpleGrid
                  columns={{ base: 1, md: 2 }}
                  spacing={4}
                  width="100%"
                >
                  <FormControl isRequired>
                    <FormLabel
                      color={labelColor}
                      fontSize="sm"
                      fontWeight="medium"
                      mb={2}
                    >
                      Full Name
                    </FormLabel>
                    <Input
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      placeholder="Enter"
                      bg={inputBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="md"
                      size="md"
                      _placeholder={{ color: "gray.400" }}
                      _focus={{
                        borderColor: "#000B8C",
                        boxShadow: "0 0 0 1px #000B8C",
                      }}
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel
                      color={labelColor}
                      fontSize="sm"
                      fontWeight="medium"
                      mb={2}
                    >
                      Organization Name
                    </FormLabel>
                    <Input
                      name="organizationName"
                      value={formData.organizationName}
                      onChange={handleInputChange}
                      placeholder="Enter"
                      bg={inputBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="md"
                      size="md"
                      _placeholder={{ color: "gray.400" }}
                      _focus={{
                        borderColor: "#000B8C",
                        boxShadow: "0 0 0 1px #000B8C",
                      }}
                    />
                  </FormControl>
                </SimpleGrid>

                <SimpleGrid
                  columns={{ base: 1, md: 2 }}
                  spacing={4}
                  width="100%"
                >
                  <FormControl isRequired>
                    <FormLabel
                      color={labelColor}
                      fontSize="sm"
                      fontWeight="medium"
                      mb={2}
                    >
                      Designation/Role
                    </FormLabel>
                    <Input
                      name="designation"
                      value={formData.designation}
                      onChange={handleInputChange}
                      placeholder="Enter"
                      bg={inputBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="md"
                      size="md"
                      _placeholder={{ color: "gray.400" }}
                      _focus={{
                        borderColor: "#000B8C",
                        boxShadow: "0 0 0 1px #000B8C",
                      }}
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel
                      color={labelColor}
                      fontSize="sm"
                      fontWeight="medium"
                      mb={2}
                    >
                      Official Email Address
                    </FormLabel>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter"
                      bg={inputBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="md"
                      size="md"
                      _placeholder={{ color: "gray.400" }}
                      _focus={{
                        borderColor: "#000B8C",
                        boxShadow: "0 0 0 1px #000B8C",
                      }}
                    />
                  </FormControl>
                </SimpleGrid>

                <FormControl>
                  <FormLabel
                    color={labelColor}
                    fontSize="sm"
                    fontWeight="medium"
                    mb={2}
                  >
                    Phone Number
                  </FormLabel>
                  <Input
                    name="phoneNumber"
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    placeholder="Enter"
                    bg={inputBg}
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius="md"
                    size="md"
                    _placeholder={{ color: "gray.400" }}
                    _focus={{
                      borderColor: "#000B8C",
                      boxShadow: "0 0 0 1px #000B8C",
                    }}
                  />
                </FormControl>

                <Text
                  fontSize="lg"
                  fontWeight="semibold"
                  color={labelColor}
                  textAlign="center"
                  mt={6}
                >
                  Partnership Type
                </Text>

                <VStack align="start" spacing={3} width="100%">
                  {[
                    { id: "channel", label: "Channel or Sales Partner" },
                    {
                      id: "government",
                      label: "Government/Digital Public Service Projects",
                    },
                  ].map((type) => (
                    <Checkbox
                      key={type.id}
                      isChecked={formData.partnershipTypes.includes(type.id)}
                      onChange={(e) =>
                        handlePartnershipTypeChange(type.id, e.target.checked)
                      }
                      colorScheme="blue"
                      size="md"
                    >
                      <Text fontSize="sm" color={labelColor}>
                        {type.label}
                      </Text>
                    </Checkbox>
                  ))}

                  <VStack align="start" spacing={2} width="100%">
                    <Checkbox
                      isChecked={formData.partnershipTypes.includes("other")}
                      onChange={(e) =>
                        handlePartnershipTypeChange("other", e.target.checked)
                      }
                      colorScheme="blue"
                      size="md"
                    >
                      <Text fontSize="sm" color={labelColor}>
                        Other:
                      </Text>
                    </Checkbox>

                    {formData.partnershipTypes.includes("other") && (
                      <Input
                        name="otherPartnershipText"
                        value={formData.otherPartnershipText}
                        onChange={handleInputChange}
                        placeholder="Please specify"
                        bg={inputBg}
                        border="1px solid"
                        borderColor={borderColor}
                        borderRadius="md"
                        size="sm"
                        ml={6}
                        maxW="300px"
                        _placeholder={{ color: "gray.400" }}
                        _focus={{
                          borderColor: "#000B8C",
                          boxShadow: "0 0 0 1px #000B8C",
                        }}
                      />
                    )}
                  </VStack>
                </VStack>

                <Text
                  fontSize="lg"
                  fontWeight="semibold"
                  color={labelColor}
                  textAlign="center"
                  mt={6}
                >
                  Organization Profile
                </Text>

                <FormControl>
                  <FormLabel
                    color={labelColor}
                    fontSize="sm"
                    fontWeight="medium"
                    mb={2}
                  >
                    Company Website
                  </FormLabel>
                  <Input
                    name="companyWebsite"
                    value={formData.companyWebsite}
                    onChange={handleInputChange}
                    placeholder="Enter"
                    bg={inputBg}
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius="md"
                    size="md"
                    _placeholder={{ color: "gray.400" }}
                    _focus={{
                      borderColor: "#000B8C",
                      boxShadow: "0 0 0 1px #000B8C",
                    }}
                  />
                </FormControl>

                <VStack align="start" spacing={2} width="100%">
                  <Checkbox
                    isChecked={formData.contactConsent}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        contactConsent: e.target.checked,
                      }))
                    }
                    colorScheme="blue"
                    size="md"
                  >
                    <Text fontSize="sm" color={labelColor}>
                      I consent to be contacted by Business Automation Limited
                      regarding this inquiry.
                    </Text>
                  </Checkbox>

                  <Checkbox
                    isChecked={formData.privacyAgreed}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        privacyAgreed: e.target.checked,
                      }))
                    }
                    colorScheme="blue"
                    size="md"
                  >
                    <Text fontSize="sm" color={labelColor}>
                      I agree to the data privacy policy and terms.
                    </Text>
                  </Checkbox>
                </VStack>

                {/* reCAPTCHA */}
                <Box width="100%" display="flex" justifyContent="center">
                  {recaptchaSiteKey ? (
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={recaptchaSiteKey}
                      onChange={handleRecaptchaChange}
                      onExpired={() => setRecaptchaToken(null)}
                      onErrored={handleRecaptchaError}
                    />
                  ) : (
                    <Text color="red.500" fontSize="sm">
                      reCAPTCHA configuration error
                    </Text>
                  )}
                </Box>

                <Button
                  type="submit"
                  bg="#000B8C"
                  color="white"
                  size="lg"
                  px={8}
                  py={3}
                  borderRadius="full"
                  fontSize="md"
                  fontWeight="medium"
                  isLoading={isSubmitting}
                  isDisabled={
                    !formData.privacyAgreed ||
                    !formData.contactConsent ||
                    isSubmitting
                  }
                  loadingText="Starting..."
                  _hover={{
                    bg:
                      !formData.privacyAgreed || !formData.contactConsent
                        ? "#000B8C"
                        : "#000A7A",
                  }}
                  _active={{
                    bg: "#000968",
                  }}
                  _disabled={{
                    bg: "gray.400",
                    color: "gray.600",
                    cursor: "not-allowed",
                    opacity: 0.6,
                  }}
                >
                  Start the Conversation
                </Button>
              </VStack>
            )}

            {activeTab === "support" && (
              <Box width="100%" py={0}>
                <Grid
                  templateColumns={{ base: "1fr", md: "1fr 1fr" }}
                  gap={{ base: 6, md: 12 }}
                  alignItems="start"
                >
                  {/* Left Column - Support Portal */}
                  <Box>
                    <Text
                      fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                      fontWeight="600"
                      color={labelColor}
                      mb={4}
                    >
                      Support Portal
                    </Text>
                    <Text
                      fontSize={{ base: "sm", md: "md" }}
                      color="gray.600"
                      mb={6}
                      lineHeight="1.6"
                    >
                      You can contact our respective expert for your necessary
                      support. Just send us the queries and we are here to help
                      you:
                    </Text>

                    <Button
                      as="a"
                      href="https://support.ba-systems.com"
                      target="_blank"
                      bg="#000B8C"
                      color="white"
                      size={{ base: "md", md: "lg" }}
                      px={{ base: 6, md: 8 }}
                      py={{ base: 3, md: 4 }}
                      borderRadius="full"
                      fontSize={{ base: "sm", md: "md" }}
                      fontWeight="500"
                      rightIcon={
                        <Box as="span" ml={2}>
                          →
                        </Box>
                      }
                      _hover={{
                        bg: "#000A7A",
                        transform: "translateY(-2px)",
                      }}
                      _active={{ bg: "#000955" }}
                      transition="all 0.3s ease"
                      boxShadow="0 2px 8px rgba(0, 11, 140, 0.2)"
                    >
                      support.ba-systems.com
                    </Button>
                  </Box>

                  {/* Right Column - Talk to Human */}
                  <Box>
                    <Text
                      fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                      fontWeight="600"
                      color={labelColor}
                      mb={6}
                    >
                      Talk to a Human (Hotline):
                    </Text>

                    <VStack spacing={{ base: 3, md: 4 }} align="stretch">
                      {/* Phone Numbers */}
                      <HStack
                        spacing={{ base: 2, md: 4 }}
                        align="center"
                        flexWrap="wrap"
                      >
                        <Box
                          borderRadius="lg"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Text fontSize={{ base: "lg", md: "xl" }}>📞</Text>
                        </Box>
                        <Text
                          color="gray.700"
                          fontSize={{ base: "md", md: "lg" }}
                          fontWeight="500"
                        >
                          +880 9602-666777
                        </Text>
                      </HStack>

                      {/* <HStack
                        spacing={{ base: 2, md: 4 }}
                        align="center"
                        flexWrap="wrap"
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Text fontSize={{ base: "lg", md: "xl" }}>📞</Text>
                        </Box>
                        <Text
                          color="gray.700"
                          fontSize={{ base: "md", md: "lg" }}
                          fontWeight="500"
                        >
                          +880 9678-771205
                        </Text>
                      </HStack>
                    */}

                      {/* Email */}
                      <HStack
                        spacing={{ base: 2, md: 4 }}
                        align="center"
                        flexWrap="wrap"
                      >
                        <Box
                          borderRadius="lg"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Text fontSize={{ base: "lg", md: "xl" }}>📧</Text>
                        </Box>
                        <Text
                          color="gray.700"
                          fontSize={{ base: "md", md: "lg" }}
                          fontWeight="500"
                          wordBreak="break-word"
                        >
                          support@ba-systems.com
                        </Text>
                      </HStack>
                    </VStack>
                  </Box>
                </Grid>
              </Box>
            )}
          </Box>
        </VStack>
      </Box>
    </Container>
  );
};

export default ContactForm;
